// Customer-entered colour codes (RAL Classic / NCS) → approximate sRGB hex for
// on-screen previews. Screen tones are indicative only — the painted finish
// is matched to the physical colour standard.

export type ColourSystem = 'RAL' | 'NCS';

export interface ColourMatch {
  system: ColourSystem;
  code: string;   // normalised, e.g. 'RAL 7016' or 'NCS S 2010-Y30R'
  hex: string;    // '#rrggbb'
  name?: string;
}

// RAL Classic — approximate sRGB values.
const RAL: Record<string, [string, string]> = {
  1000: ['#BEBD7F', 'Green beige'], 1001: ['#C2B078', 'Beige'], 1002: ['#C6A664', 'Sand yellow'],
  1003: ['#E5BE01', 'Signal yellow'], 1004: ['#CDA434', 'Golden yellow'], 1005: ['#A98307', 'Honey yellow'],
  1006: ['#E4A010', 'Maize yellow'], 1007: ['#DC9D00', 'Daffodil yellow'], 1011: ['#8A6642', 'Brown beige'],
  1012: ['#C7B446', 'Lemon yellow'], 1013: ['#EAE6CA', 'Oyster white'], 1014: ['#E1CC4F', 'Ivory'],
  1015: ['#E6D690', 'Light ivory'], 1016: ['#EDFF21', 'Sulfur yellow'], 1017: ['#F5D033', 'Saffron yellow'],
  1018: ['#F8F32B', 'Zinc yellow'], 1019: ['#9E9764', 'Grey beige'], 1020: ['#999950', 'Olive yellow'],
  1021: ['#F3DA0B', 'Rape yellow'], 1023: ['#FAD201', 'Traffic yellow'], 1024: ['#AEA04B', 'Ochre yellow'],
  1026: ['#FFFF00', 'Luminous yellow'], 1027: ['#9D9101', 'Curry'], 1028: ['#F4A900', 'Melon yellow'],
  1032: ['#D6AE01', 'Broom yellow'], 1033: ['#F3A505', 'Dahlia yellow'], 1034: ['#EFA94A', 'Pastel yellow'],
  1035: ['#6A5D4D', 'Pearl beige'], 1036: ['#705335', 'Pearl gold'], 1037: ['#F39F18', 'Sun yellow'],
  2000: ['#ED760E', 'Yellow orange'], 2001: ['#C93C20', 'Red orange'], 2002: ['#CB2821', 'Vermilion'],
  2003: ['#FF7514', 'Pastel orange'], 2004: ['#F44611', 'Pure orange'], 2005: ['#FF2301', 'Luminous orange'],
  2007: ['#FFA420', 'Luminous bright orange'], 2008: ['#F75E25', 'Bright red orange'], 2009: ['#F54021', 'Traffic orange'],
  2010: ['#D84B20', 'Signal orange'], 2011: ['#EC7C26', 'Deep orange'], 2012: ['#E55137', 'Salmon orange'],
  2013: ['#C35831', 'Pearl orange'], 2017: ['#FA4402', 'RAL orange'],
  3000: ['#AF2B1E', 'Flame red'], 3001: ['#A52019', 'Signal red'], 3002: ['#A2231D', 'Carmine red'],
  3003: ['#9B111E', 'Ruby red'], 3004: ['#75151E', 'Purple red'], 3005: ['#5E2129', 'Wine red'],
  3007: ['#412227', 'Black red'], 3009: ['#642424', 'Oxide red'], 3011: ['#781F19', 'Brown red'],
  3012: ['#C1876B', 'Beige red'], 3013: ['#A12312', 'Tomato red'], 3014: ['#D36E70', 'Antique pink'],
  3015: ['#EA899A', 'Light pink'], 3016: ['#B32821', 'Coral red'], 3017: ['#E63244', 'Rose'],
  3018: ['#D53032', 'Strawberry red'], 3020: ['#CC0605', 'Traffic red'], 3022: ['#D95030', 'Salmon pink'],
  3024: ['#F80000', 'Luminous red'], 3026: ['#FE0000', 'Luminous bright red'], 3027: ['#C51D34', 'Raspberry red'],
  3028: ['#CB3234', 'Pure red'], 3031: ['#B32428', 'Orient red'], 3032: ['#721422', 'Pearl ruby red'],
  3033: ['#B44C43', 'Pearl pink'],
  4001: ['#6D3F5B', 'Red lilac'], 4002: ['#922B3E', 'Red violet'], 4003: ['#DE4C8A', 'Heather violet'],
  4004: ['#641C34', 'Claret violet'], 4005: ['#6C4675', 'Blue lilac'], 4006: ['#A03472', 'Traffic purple'],
  4007: ['#4A192C', 'Purple violet'], 4008: ['#924E7D', 'Signal violet'], 4009: ['#A18594', 'Pastel violet'],
  4010: ['#CF3476', 'Telemagenta'], 4011: ['#8673A1', 'Pearl violet'], 4012: ['#6C6874', 'Pearl blackberry'],
  5000: ['#354D73', 'Violet blue'], 5001: ['#1F3438', 'Green blue'], 5002: ['#20214F', 'Ultramarine blue'],
  5003: ['#1D1E33', 'Sapphire blue'], 5004: ['#18171C', 'Black blue'], 5005: ['#1E2460', 'Signal blue'],
  5007: ['#3E5F8A', 'Brilliant blue'], 5008: ['#26252D', 'Grey blue'], 5009: ['#025669', 'Azure blue'],
  5010: ['#0E294B', 'Gentian blue'], 5011: ['#231A24', 'Steel blue'], 5012: ['#3B83BD', 'Light blue'],
  5013: ['#1E213D', 'Cobalt blue'], 5014: ['#606E8C', 'Pigeon blue'], 5015: ['#2271B3', 'Sky blue'],
  5017: ['#063971', 'Traffic blue'], 5018: ['#3F888F', 'Turquoise blue'], 5019: ['#1B5583', 'Capri blue'],
  5020: ['#1D334A', 'Ocean blue'], 5021: ['#256D7B', 'Water blue'], 5022: ['#252850', 'Night blue'],
  5023: ['#49678D', 'Distant blue'], 5024: ['#5D9B9B', 'Pastel blue'], 5025: ['#2A6478', 'Pearl gentian blue'],
  5026: ['#102C54', 'Pearl night blue'],
  6000: ['#316650', 'Patina green'], 6001: ['#287233', 'Emerald green'], 6002: ['#2D572C', 'Leaf green'],
  6003: ['#424632', 'Olive green'], 6004: ['#1F3A3D', 'Blue green'], 6005: ['#2F4538', 'Moss green'],
  6006: ['#3E3B32', 'Grey olive'], 6007: ['#343B29', 'Bottle green'], 6008: ['#39352A', 'Brown green'],
  6009: ['#31372B', 'Fir green'], 6010: ['#35682D', 'Grass green'], 6011: ['#587246', 'Reseda green'],
  6012: ['#343E40', 'Black green'], 6013: ['#6C7156', 'Reed green'], 6014: ['#47402E', 'Yellow olive'],
  6015: ['#3B3C36', 'Black olive'], 6016: ['#1E5945', 'Turquoise green'], 6017: ['#4C9141', 'May green'],
  6018: ['#57A639', 'Yellow green'], 6019: ['#BDECB6', 'Pastel green'], 6020: ['#2E3A23', 'Chrome green'],
  6021: ['#89AC76', 'Pale green'], 6022: ['#25221B', 'Olive drab'], 6024: ['#308446', 'Traffic green'],
  6025: ['#3D642D', 'Fern green'], 6026: ['#015D52', 'Opal green'], 6027: ['#84C3BE', 'Light green'],
  6028: ['#2C5545', 'Pine green'], 6029: ['#20603D', 'Mint green'], 6032: ['#317F43', 'Signal green'],
  6033: ['#497E76', 'Mint turquoise'], 6034: ['#7FB5B5', 'Pastel turquoise'], 6035: ['#1C542D', 'Pearl green'],
  6036: ['#193737', 'Pearl opal green'], 6037: ['#008F39', 'Pure green'], 6038: ['#00BB2D', 'Luminous green'],
  6039: ['#B5BA2F', 'Fibrous green'],
  7000: ['#78858B', 'Squirrel grey'], 7001: ['#8A9597', 'Silver grey'], 7002: ['#7E7B52', 'Olive grey'],
  7003: ['#6C7059', 'Moss grey'], 7004: ['#969992', 'Signal grey'], 7005: ['#646B63', 'Mouse grey'],
  7006: ['#6D6552', 'Beige grey'], 7008: ['#6A5F31', 'Khaki grey'], 7009: ['#4D5645', 'Green grey'],
  7010: ['#4C514A', 'Tarpaulin grey'], 7011: ['#434B4D', 'Iron grey'], 7012: ['#4E5754', 'Basalt grey'],
  7013: ['#464531', 'Brown grey'], 7015: ['#434750', 'Slate grey'], 7016: ['#383E42', 'Anthracite grey'],
  7021: ['#2E3234', 'Black grey'], 7022: ['#332F2C', 'Umbra grey'], 7023: ['#686C5E', 'Concrete grey'],
  7024: ['#474A50', 'Graphite grey'], 7026: ['#2F353B', 'Granite grey'], 7030: ['#8B8C7A', 'Stone grey'],
  7031: ['#474B4E', 'Blue grey'], 7032: ['#B8B799', 'Pebble grey'], 7033: ['#7D8471', 'Cement grey'],
  7034: ['#8F8B66', 'Yellow grey'], 7035: ['#CBD0CC', 'Light grey'], 7036: ['#7F7679', 'Platinum grey'],
  7037: ['#7D7F7D', 'Dusty grey'], 7038: ['#B5B8B1', 'Agate grey'], 7039: ['#6C6960', 'Quartz grey'],
  7040: ['#9DA1AA', 'Window grey'], 7042: ['#8D948D', 'Traffic grey A'], 7043: ['#4E5452', 'Traffic grey B'],
  7044: ['#CAC4B0', 'Silk grey'], 7045: ['#909090', 'Telegrey 1'], 7046: ['#82898F', 'Telegrey 2'],
  7047: ['#D0D0D0', 'Telegrey 4'], 7048: ['#898176', 'Pearl mouse grey'],
  8000: ['#826C34', 'Green brown'], 8001: ['#955F20', 'Ochre brown'], 8002: ['#6C3B2A', 'Signal brown'],
  8003: ['#734222', 'Clay brown'], 8004: ['#8E402A', 'Copper brown'], 8007: ['#59351F', 'Fawn brown'],
  8008: ['#6F4F28', 'Olive brown'], 8011: ['#5B3A29', 'Nut brown'], 8012: ['#592321', 'Red brown'],
  8014: ['#382C1E', 'Sepia brown'], 8015: ['#633A34', 'Chestnut brown'], 8016: ['#4C2F27', 'Mahogany brown'],
  8017: ['#45322E', 'Chocolate brown'], 8019: ['#403A3A', 'Grey brown'], 8022: ['#212121', 'Black brown'],
  8023: ['#A65E2E', 'Orange brown'], 8024: ['#79553D', 'Beige brown'], 8025: ['#755C48', 'Pale brown'],
  8028: ['#4E3B31', 'Terra brown'], 8029: ['#763C28', 'Pearl copper'],
  9001: ['#E9E0D2', 'Cream'], 9002: ['#E7EBDA', 'Grey white'], 9003: ['#ECECE7', 'Signal white'],
  9004: ['#282828', 'Signal black'], 9005: ['#0A0A0D', 'Jet black'], 9006: ['#A5A5A5', 'White aluminium'],
  9007: ['#8F8F8F', 'Grey aluminium'], 9010: ['#F1ECE1', 'Pure white'], 9011: ['#1C1C1C', 'Graphite black'],
  9012: ['#F8F2E1', 'Cleanroom white'], 9016: ['#F1F0EA', 'Traffic white'], 9017: ['#1E1E1E', 'Traffic black'],
  9018: ['#CFD3CD', 'Papyrus white'], 9022: ['#9C9C9C', 'Pearl light grey'], 9023: ['#828282', 'Pearl dark grey']
};

const toHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

export const lookupRal = (input: string): ColourMatch | null => {
  const m = input.toUpperCase().replace(/\s+/g, '').match(/^(?:RAL)?(\d{4})$/);
  if (!m || !RAL[m[1]]) return null;
  const [hex, name] = RAL[m[1]];
  return { system: 'RAL', code: `RAL ${m[1]}`, hex: hex.toLowerCase(), name };
};

// Hue circle order — a mixed hue must step to the next primary (Y→R→B→G→Y).
const NEXT_HUE: Record<string, string> = { Y: 'R', R: 'B', B: 'G', G: 'Y' };

// Approximate NCS → sRGB conversion from the NCS notation itself
// (blackness, chromaticness, hue). Accepts 'S 2010-Y30R', 'NCS S2010-Y30R',
// '2010Y30R', 'S 0500-N' etc.
export const lookupNcs = (input: string): ColourMatch | null => {
  const m = input.toUpperCase().replace(/\s+/g, '')
    .match(/^(?:NCS)?S?(\d{2})(\d{2})-?(N|([YRBG])(?:(\d{2})([YRBG]))?)$/);
  if (!m) return null;
  const black = Number(m[1]);
  const chroma = Number(m[2]);
  if (black + chroma > 100) return null;
  const hue = m[3];
  const code = `NCS S ${m[1]}${m[2]}-${hue}`;

  if (hue === 'N') {
    if (chroma !== 0) return null;
    const grey = (1 - black / 100) * 255;
    return { system: 'NCS', code, hex: toHex(grey, grey, grey) };
  }

  const bc = m[4];
  let pct = 0;
  if (m[5]) {
    pct = Number(m[5]);
    if (pct < 1 || pct > 99 || NEXT_HUE[bc] !== m[6]) return null;
  }

  let red = 0;
  let green = 0;
  let blue = 0;
  let f: number;
  // Red component
  if (bc === 'Y' && pct <= 60) red = 1;
  else if ((bc === 'Y' && pct > 60) || (bc === 'R' && pct <= 80)) {
    f = bc === 'Y' ? pct - 60 : pct + 40;
    red = (Math.sqrt(14884 - f * f) - 22) / 100;
  } else if ((bc === 'R' && pct > 80) || bc === 'B') red = 0;
  else if (bc === 'G') {
    f = pct - 170;
    red = (Math.sqrt(33800 - f * f) - 70) / 100;
  }
  // Blue component
  if (bc === 'Y' && pct <= 80) blue = 0;
  else if ((bc === 'Y' && pct > 80) || (bc === 'R' && pct <= 60)) {
    f = bc === 'Y' ? pct - 80 + 20.5 : pct + 20 + 20.5;
    blue = (104 - Math.sqrt(11236 - f * f)) / 100;
  } else if ((bc === 'R' && pct > 60) || (bc === 'B' && pct <= 80)) {
    f = bc === 'R' ? pct - 60 - 60 : pct + 40 - 60;
    blue = (Math.sqrt(10000 - f * f) - 10) / 100;
  } else if ((bc === 'B' && pct > 80) || (bc === 'G' && pct <= 40)) {
    f = bc === 'B' ? pct - 80 - 131 : pct + 20 - 131;
    blue = (122 - Math.sqrt(19881 - f * f)) / 100;
  } else if (bc === 'G' && pct > 40) blue = 0;
  // Green component
  if (bc === 'Y') green = (85 - (17 / 20) * pct) / 100;
  else if (bc === 'R' && pct <= 60) green = 0;
  else if (bc === 'R' && pct > 60) {
    f = pct - 60 + 35;
    green = (67.5 - Math.sqrt(5776 - f * f)) / 100;
  } else if (bc === 'B' && pct <= 60) {
    f = pct - 68.5;
    green = (6.5 + Math.sqrt(7044.5 - f * f)) / 100;
  } else if ((bc === 'B' && pct > 60) || (bc === 'G' && pct <= 60)) green = 0.9;
  else if (bc === 'G' && pct > 60) green = (90 - (pct - 60) / 8) / 100;

  // Desaturate towards grey by chromaticness, then scale by blackness.
  const avg = (red + green + blue) / 3;
  const r2 = (avg - red) * (100 - chroma) / 100 + red;
  const g2 = (avg - green) * (100 - chroma) / 100 + green;
  const b2 = (avg - blue) * (100 - chroma) / 100 + blue;
  const max = Math.max(r2, g2, b2) || 1;
  const light = (100 - (1.05 * black - 5.25)) / 100;
  return { system: 'NCS', code, hex: toHex(r2 / max * light * 255, g2 / max * light * 255, b2 / max * light * 255) };
};

export const lookupColourCode = (system: ColourSystem, input: string): ColourMatch | null =>
  !input.trim() ? null : system === 'RAL' ? lookupRal(input) : lookupNcs(input);

// Option id → colour system for the custom-colour options.
export const CUSTOM_COLOUR_SYSTEM: Record<string, ColourSystem> = {
  colour_custom_ncs: 'NCS',
  colour_custom_ral: 'RAL'
};
